import { BPCategory, BPCategoryMeta } from '@/types/record';

/**
 * AHA / ACC / WHO Blood Pressure Clinical Categories Definitions
 */
export const BP_CATEGORIES_METADATA: Record<BPCategory, BPCategoryMeta> = {
  CRISIS: {
    category: 'CRISIS',
    labelZh: '高血壓危象',
    labelEn: 'Hypertensive Crisis',
    shortLabel: '危象',
    description: '收縮壓 > 180 或 舒張壓 > 120 mmHg',
    recommendation: '血壓已達急診危險警戒值，請稍候再測一次；若持續過高請立即就醫！',
    stampBg: 'bg-red-900 text-red-50',
    stampBorder: 'border-red-900',
    stampText: 'text-red-950',
    chartColor: '#7f1d1d',
  },
  STAGE_2: {
    category: 'STAGE_2',
    labelZh: '二期高血壓',
    labelEn: 'Stage 2 Hypertension',
    shortLabel: '二期高',
    description: '收縮壓 ≥ 140 或 舒張壓 ≥ 90 mmHg',
    recommendation: '血壓明顯過高，建議遵循醫師處方按時服藥並規律記錄。',
    stampBg: 'bg-red-50',
    stampBorder: 'border-red-700',
    stampText: 'text-red-800',
    chartColor: '#dc2626',
  },
  STAGE_1: {
    category: 'STAGE_1',
    labelZh: '一期高血壓',
    labelEn: 'Stage 1 Hypertension',
    shortLabel: '一期高',
    description: '收縮壓 130–139 或 舒張壓 80–89 mmHg',
    recommendation: '血壓偏高進入一期範圍，應注重低鹽飲食、減壓與規律作息。',
    stampBg: 'bg-orange-50',
    stampBorder: 'border-orange-600',
    stampText: 'text-orange-950',
    chartColor: '#ea580c',
  },
  ELEVATED: {
    category: 'ELEVATED',
    labelZh: '血壓偏高',
    labelEn: 'Elevated',
    shortLabel: '偏高',
    description: '收縮壓 120–129 且 舒張壓 < 80 mmHg',
    recommendation: '尚未達高血壓診斷，但收縮壓偏高，需維持運動與健康生活型態。',
    stampBg: 'bg-amber-50',
    stampBorder: 'border-amber-600',
    stampText: 'text-amber-900',
    chartColor: '#ca8a04',
  },
  NORMAL: {
    category: 'NORMAL',
    labelZh: '正常血壓',
    labelEn: 'Normal',
    shortLabel: '正常',
    description: '收縮壓 < 120 且 舒張壓 < 80 mmHg',
    recommendation: '血壓處於理想標準範圍，請繼續保持健康的生活與飲食習慣。',
    stampBg: 'bg-emerald-50',
    stampBorder: 'border-emerald-700',
    stampText: 'text-emerald-900',
    chartColor: '#166534',
  },
};

/**
 * Classify a blood pressure reading according to AHA/ACC 2017 Guidelines
 *
 * Evaluation rules (hierarchical order):
 * 1. Hypertensive Crisis: Systolic > 180 OR Diastolic > 120
 * 2. Stage 2 Hypertension: Systolic >= 140 OR Diastolic >= 90
 * 3. Stage 1 Hypertension: (130 <= Systolic <= 139) OR (80 <= Diastolic <= 89)
 * 4. Elevated: (120 <= Systolic <= 129) AND Diastolic < 80
 * 5. Normal: Systolic < 120 AND Diastolic < 80
 */
export function classifyBP(systolic: number, diastolic: number): BPCategoryMeta {
  if (systolic > 180 || diastolic > 120) {
    return BP_CATEGORIES_METADATA.CRISIS;
  }
  if (systolic >= 140 || diastolic >= 90) {
    return BP_CATEGORIES_METADATA.STAGE_2;
  }
  if ((systolic >= 130 && systolic <= 139) || (diastolic >= 80 && diastolic <= 89)) {
    return BP_CATEGORIES_METADATA.STAGE_1;
  }
  if (systolic >= 120 && systolic <= 129 && diastolic < 80) {
    return BP_CATEGORIES_METADATA.ELEVATED;
  }
  return BP_CATEGORIES_METADATA.NORMAL;
}

/**
 * Get category metadata by category key
 */
export function getCategoryMeta(category: BPCategory): BPCategoryMeta {
  return BP_CATEGORIES_METADATA[category] || BP_CATEGORIES_METADATA.NORMAL;
}

/**
 * Calculate Pulse Pressure (脈壓差) = Systolic - Diastolic
 * Normal range is generally 30 to 50 mmHg. Values > 60 mmHg may indicate vascular stiffness.
 */
export function calculatePulsePressure(systolic: number, diastolic: number): number {
  return systolic - diastolic;
}

/**
 * Calculate Mean Arterial Pressure (平均動脈壓 MAP)
 * Formula: DBP + (SBP - DBP) / 3
 * Normal range is typically 70 to 100 mmHg.
 */
export function calculateMAP(diastolic: number, systolic: number): number {
  const pp = systolic - diastolic;
  return Math.round(diastolic + pp / 3);
}

/**
 * Validate physiological plausibility of BP & Pulse input
 */
export function validateBPValues(
  systolic: number,
  diastolic: number,
  pulse: number
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (isNaN(systolic) || systolic < 40 || systolic > 300) {
    errors.push('收縮壓數值需介於 40 至 300 mmHg 之間');
  }

  if (isNaN(diastolic) || diastolic < 30 || diastolic > 200) {
    errors.push('舒張壓數值需介於 30 至 200 mmHg 之間');
  }

  if (isNaN(pulse) || pulse < 30 || pulse > 250) {
    errors.push('心跳數值需介於 30 至 250 bpm 之間');
  }

  if (!isNaN(systolic) && !isNaN(diastolic) && systolic <= diastolic) {
    errors.push('收縮壓必須高於舒張壓');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Return all categories as an ordered list from Normal to Crisis for charts & legends
 */
export function getAllCategories(): BPCategoryMeta[] {
  return [
    BP_CATEGORIES_METADATA.NORMAL,
    BP_CATEGORIES_METADATA.ELEVATED,
    BP_CATEGORIES_METADATA.STAGE_1,
    BP_CATEGORIES_METADATA.STAGE_2,
    BP_CATEGORIES_METADATA.CRISIS,
  ];
}
